/**
 * Friendly messages for backend domain error codes (Phase 4: clinical record integrity).
 *
 * The backend puts a domain code in `error.code` (INVALID_TRANSITION, CHART_LOCKED, ...);
 * apiClient keeps it on `ApiError.domainCode`. `describeApiError` turns any thrown value into
 * a short message that is safe to show in a toast or dialog. It never includes record content.
 */

import { ApiError, isApiError } from '../dtos/common/api.dto';
import { capReachedMessage } from './subscription';
import { providerTypeCodes } from '../constants/disciplines';

const PROVIDER_TYPE_LIST = (() => {
  const codes = providerTypeCodes();
  return codes.length > 1 ? `${codes.slice(0, -1).join(', ')} or ${codes[codes.length - 1]}` : codes.join('');
})();

export type DomainErrorCode =
  | 'INVALID_TRANSITION'
  | 'RECORD_LOCKED'
  | 'CHART_LOCKED'
  | 'ENTRY_INVALIDATED'
  | 'SELF_APPROVAL'
  | 'NOT_ENTRY_AUTHOR'
  | 'REASON_REQUIRED'
  | 'REQUIRED_FIELDS_MISSING'
  | 'VISIT_SUMMARY_REQUIRED'
  | 'SIGNED_DOCUMENT_REQUIRED'
  | 'STATUS_CHANGE_NOT_ALLOWED'
  | 'NO_VALID_FIELDS'
  // Phase 5: sign-in, legal and BAA
  | 'ACCOUNT_LOCKED'
  | 'INVALID_CREDENTIALS'
  | 'CAPTCHA_REQUIRED'
  | 'CAPTCHA_INVALID'
  | 'LEGAL_VERSION_STALE'
  | 'BAA_NOT_ACCEPTED'
  // Phase 5: user create/update
  | 'PROVIDER_TYPE_REQUIRED'
  | 'INVALID_PROVIDER_TYPE'
  | 'INVALID_LICENSE_EXPIRY'
  | 'INVALID_LICENSE_NUMBER'
  | 'INVALID_STATUS'
  // Phase 13: subscriptions and payments
  | 'ACCOUNT_READ_ONLY'
  | 'ACCOUNT_SUSPENDED'
  | 'ACCOUNT_CLOSED'
  | 'PATIENT_CAP_REACHED'
  | 'BILLING_DISABLED_DEMO'
  | 'DOWNGRADE_OVER_CAP'
  | 'PLAN_NOT_SELLABLE'
  | 'PAYMENT_REQUIRED'
  | 'CANCEL_SCHEDULED'
  | 'SAME_PLAN'
  | 'SAMPLE_EXISTS'
  | 'NOT_SAMPLE'
  // Phase 14: data exports
  | 'PURPOSE_REQUIRED'
  | 'REAUTH_REQUIRED'
  | 'EXPORT_IN_PROGRESS'
  | 'EXPORT_NOT_READY'
  | 'EXPORT_EXPIRED'
  | 'NOT_REQUESTER'
  | 'SAMPLE_PATIENT'
  | 'AUDIT_WRITE_FAILED'
  // Phase 16E: discharge notice and discharge / transfer summaries
  | 'DISCHARGE_NOTICE_TOO_SHORT'
  | 'DISCHARGE_NOTICE_INVALID'
  | 'NOT_AUTHOR'
  | 'VERSION_CONFLICT'
  | 'AMEND_REASON_REQUIRED'
  | 'CLOSE_REASON_REQUIRED'
  | 'NO_CHANGES'
  | 'SUMMARY_EXISTS'
  | 'PATIENT_NOT_DISCHARGED'
  // Phase 18: charting MVP (M2 correction window, M4 end-visit gate, F21 supervisory note)
  | 'CHART_EDIT_WINDOW_CLOSED'
  | 'CHART_EMPTY'
  | 'SIGNER_REQUIRED'
  | 'NO_CARE_INVALID'
  | 'SUPERVISORY_RN_ONLY'
  | 'SUPERVISORY_NOTE_REQUIRED'
  // Phase 18: chart entry forms (M1 validation, F21 supervisory note on a non-supervisory visit)
  | 'ENTRY_INVALID'
  | 'ENTRY_NOT_ALLOWED'
  // Phase 20C: therapy PAs
  | 'THERAPY_ACUITY_REQUIRED'
  | 'THERAPY_PERIOD_TOO_LONG'
  | 'INVALID_THERAPY_ACUITY'
  // Phase 20D: attendant PAs
  | 'ATTENDANT_PROGRAM_REQUIRED'
  | 'INVALID_ATTENDANT_PROGRAM'
  | 'PACKET_NOT_APPLICABLE'
  // Phase 20F: patient programmes
  | 'INVALID_PROGRAM';

const DOMAIN_MESSAGES: Record<DomainErrorCode, string> = {
  INVALID_TRANSITION: 'This action is not allowed in the record\'s current status. Refresh to see the latest status.',
  RECORD_LOCKED: 'This record is locked and can only be edited while it is a draft. Reopen it to make changes.',
  CHART_LOCKED: 'This chart has been submitted and is locked. Reopen the chart to add or change entries.',
  ENTRY_INVALIDATED: 'This entry has been invalidated and can no longer be changed.',
  SELF_APPROVAL: 'You cannot approve or review a record you authored or submitted. Another reviewer must do this.',
  NOT_ENTRY_AUTHOR: 'Only the author of this entry can edit it.',
  REASON_REQUIRED: 'A reason of at least 5 characters is required.',
  REQUIRED_FIELDS_MISSING: 'Some required fields are missing. Complete them before submitting.',
  VISIT_SUMMARY_REQUIRED: 'Add a visit summary entry before submitting this chart.',
  SIGNED_DOCUMENT_REQUIRED: 'Upload the signed document before marking this as signed.',
  STATUS_CHANGE_NOT_ALLOWED: 'Status can only be changed with the workflow actions (Submit, Approve, Deny, Pend).',
  NO_VALID_FIELDS: 'There were no changes the server could save.',
  ACCOUNT_LOCKED: 'Too many failed sign-in attempts. Try again in 15 minutes.',
  INVALID_CREDENTIALS: 'Incorrect username or password',
  CAPTCHA_REQUIRED: 'Complete the verification to continue.',
  CAPTCHA_INVALID: 'Verification failed or expired. Try again.',
  LEGAL_VERSION_STALE: 'These terms were updated while you were reviewing them. Review the latest version and accept again.',
  BAA_NOT_ACCEPTED: 'Patient record editing is paused until an owner or admin signs the updated Business Associate Agreement.',
  PROVIDER_TYPE_REQUIRED: `Clinician and Clinical Manager users need a provider type (${PROVIDER_TYPE_LIST}).`,
  INVALID_PROVIDER_TYPE: `Select a valid provider type: ${PROVIDER_TYPE_LIST}.`,
  INVALID_LICENSE_EXPIRY: 'The license expiry date is not valid. Use a real date (YYYY-MM-DD).',
  INVALID_LICENSE_NUMBER: 'The license number is not valid. Check it and try again.',
  INVALID_STATUS: 'Status must be Active or Inactive.',
  ACCOUNT_READ_ONLY: 'Your account is read-only. You can view and export data. Choose a plan to make changes again.',
  ACCOUNT_SUSPENDED: 'Your account is suspended. Only data exports are available. Choose a plan to restore access.',
  ACCOUNT_CLOSED: 'This account is closed. The data export window has ended.',
  PATIENT_CAP_REACHED: "You've reached the patient limit on your plan. Upgrade to admit more.",
  BILLING_DISABLED_DEMO: 'Billing is not enabled for this demo account.',
  DOWNGRADE_OVER_CAP: 'You have more active patients than the new plan allows. Discharge patients or choose a larger plan.',
  PLAN_NOT_SELLABLE: 'That plan is not available yet. Choose another plan or contact sales.',
  PAYMENT_REQUIRED: 'Your last payment failed. Replace your card to change plan.',
  CANCEL_SCHEDULED: 'A cancellation is scheduled. Undo the cancellation to change plan.',
  SAME_PLAN: 'You are already on this plan.',
  SAMPLE_EXISTS: 'A sample patient is already loaded.',
  NOT_SAMPLE: 'Only the sample patient can be deleted this way.',
  PURPOSE_REQUIRED: 'Enter the purpose of the export or who will receive it.',
  REAUTH_REQUIRED: 'For security, sign in again before starting an agency export, then start it within 5 minutes.',
  EXPORT_IN_PROGRESS: 'An agency export is already being prepared. Wait for it to finish.',
  EXPORT_NOT_READY: 'The export is still being prepared.',
  EXPORT_EXPIRED: 'The download window for this export has ended. Start a new export.',
  NOT_REQUESTER: 'Only the person who requested this export can download it.',
  SAMPLE_PATIENT: 'The sample patient is practice data and is not exported.',
  AUDIT_WRITE_FAILED: 'The audit log is unavailable, so the export was stopped. Try again shortly.',
  DISCHARGE_NOTICE_TOO_SHORT: 'Written notice must be given at least 5 days before discharge (8 working days if mailed, with phone or in-person contact 5 days before). Change the dates or choose the exception that applies.',
  DISCHARGE_NOTICE_INVALID: 'Complete the discharge notice: notice date, method and recipient, and when and how the physician was told.',
  NOT_AUTHOR: 'Only the author of this summary can change or sign it.',
  VERSION_CONFLICT: 'Someone else changed this summary. Reload it and try again.',
  AMEND_REASON_REQUIRED: 'Give a reason for the amendment (10 to 500 characters).',
  CLOSE_REASON_REQUIRED: 'Give a reason (10 to 500 characters).',
  NO_CHANGES: 'Nothing changed. Edit a field before saving the amendment.',
  SUMMARY_EXISTS: 'A summary for this discharge already exists.',
  PATIENT_NOT_DISCHARGED: 'A discharge summary needs a discharged patient.',
  CHART_EDIT_WINDOW_CLOSED: 'The correction window has closed. Ask a supervisor to reopen the chart.',
  CHART_EMPTY: 'This visit has no chart entries. Chart the care you gave, or choose "No care provided" with a reason.',
  SIGNER_REQUIRED: 'Enter who signed and their relationship to the patient, or choose "Unable to sign" with a reason.',
  SUPERVISORY_NOTE_REQUIRED: 'Add the supervisory note before signing.',
  NO_CARE_INVALID: 'Choose why no care was provided and describe what happened (at least 10 characters).',
  SUPERVISORY_RN_ONLY: 'Only a registered nurse (RN) or a licensed therapist (PT, OT, ST) can start a supervisory visit.',
  ENTRY_INVALID: 'Some values are not valid. Check the highlighted fields.',
  ENTRY_NOT_ALLOWED: 'This entry type is not allowed on this visit. A supervisory note needs a supervisory visit, a treatment note a PT, OT or ST visit, and an aide task note an aide or attendant visit.',
  THERAPY_ACUITY_REQUIRED: 'Say whether this therapy request is acute or chronic before submitting it.',
  THERAPY_PERIOD_TOO_LONG: 'The requested period is too long: an acute therapy request covers at most 60 days and a chronic one 180.',
  INVALID_THERAPY_ACUITY: 'Choose acute or chronic.',
  ATTENDANT_PROGRAM_REQUIRED: 'Choose the attendant programme the MCO authorized (PCS, CFC personal assistance or CFC habilitation).',
  INVALID_ATTENDANT_PROGRAM: 'Choose PCS, CFC personal assistance or CFC habilitation.',
  PACKET_NOT_APPLICABLE: 'Attendant care has no PA packet: the MCO assesses and authorizes it. Record the authorization on the PA.',
  INVALID_PROGRAM: 'Some values are not valid. Check the dates, the NPI (10 digits) and the required fields.',
};

const STATUS_MESSAGES: Record<number, string> = {
  0: 'Network error. Check your connection and try again.',
  400: 'The request was not valid. Check the form and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: 'You do not have permission to do this.',
  404: 'The record was not found. It may have been removed.',
  408: 'The request timed out. Please try again.',
  409: 'This record changed or is in a state that does not allow this action. Refresh and try again.',
  422: 'Some required information is missing.',
  423: 'Too many failed sign-in attempts. Try again in 15 minutes.',
  429: 'Too many requests. Please wait a moment and try again.',
};

function isDomainErrorCode(code: string | undefined): code is DomainErrorCode {
  return !!code && Object.prototype.hasOwnProperty.call(DOMAIN_MESSAGES, code);
}

/** The backend domain code, if the error carries one. */
export function getApiErrorCode(err: unknown): string | undefined {
  return isApiError(err) ? err.domainCode : undefined;
}

/** HTTP status of an ApiError, or undefined for other errors. */
export function getApiErrorStatus(err: unknown): number | undefined {
  return isApiError(err) ? err.statusCode : undefined;
}

/** True when the error has the given domain code. */
export function hasApiErrorCode(err: unknown, code: DomainErrorCode): boolean {
  return getApiErrorCode(err) === code;
}

/** Missing required field names from a 422 REQUIRED_FIELDS_MISSING response (empty otherwise). */
export function getMissingFields(err: unknown): string[] {
  if (!isApiError(err)) return [];
  const missing = (err as ApiError).details?.missing;
  return Array.isArray(missing) ? missing.filter((m): m is string => typeof m === 'string') : [];
}

/**
 * Phase 18 M1: field problems from a 400 ENTRY_INVALID response, keyed by data path
 * (`fields: [{ field, message }]`, or a single `field`). Empty for any other error.
 */
export function getFieldErrors(err: unknown): Record<string, string> {
  if (!isApiError(err)) return {};
  const details = (err as ApiError).details || {};
  const out: Record<string, string> = {};
  const fields = details.fields;
  if (Array.isArray(fields)) {
    for (const f of fields) {
      if (f && typeof f === 'object' && typeof (f as { field?: unknown }).field === 'string') {
        const item = f as { field: string; message?: unknown };
        out[item.field] = typeof item.message === 'string' && item.message ? item.message : 'Not valid';
      }
    }
  }
  if (typeof details.field === 'string' && !out[details.field]) {
    out[details.field] = err instanceof Error && err.message ? err.message : 'Not valid';
  }
  return out;
}

/**
 * Turn any thrown value into a friendly, PHI-free message.
 * @param err the caught error
 * @param fallback message used when nothing more specific is known
 */
export function describeApiError(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (isApiError(err)) {
    const code = err.domainCode;
    if (isDomainErrorCode(code)) {
      const base = DOMAIN_MESSAGES[code];
      if (code === 'PATIENT_CAP_REACHED') {
        return capReachedMessage(err.details);
      }
      if (code === 'DOWNGRADE_OVER_CAP') {
        const used = err.details?.used;
        const limit = err.details?.limit;
        return typeof used === 'number' && typeof limit === 'number'
          ? `You have ${used} active patients; the new plan allows ${limit}. Discharge patients or choose a larger plan.`
          : base;
      }
      if (code === 'REQUIRED_FIELDS_MISSING') {
        const missing = getMissingFields(err);
        return missing.length > 0 ? `${base} Missing: ${missing.join(', ')}.` : base;
      }
      return base;
    }
    if (STATUS_MESSAGES[err.statusCode]) {
      return STATUS_MESSAGES[err.statusCode];
    }
    if (err.statusCode >= 500) {
      return 'The server had a problem. Please try again later.';
    }
    return fallback;
  }
  return fallback;
}
